
export interface AiValidationHistoryRow {
  id: string;
  name: string;
  type: string;
  runType: string;
  assetType: string;
  stage: string;
  config: string;
  status: string;
  confidenceScore: string;
  latency: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  date: string;
  tokens: number;
  cost: number;
  preChecks: string;
  precheckPassCount: string;
  preCheckResults: PreCheckResult[];
  piiData: PiiFlaggingData | null;
  blurFlaggingData: BlurFlaggingData | null;
  duplicateCheckData: DuplicateCheckData | null;
  blurCheck: boolean | null;
  validationSummary: ValidationSummary | null;
  aiValidationResponse: string;
  imageInfo: {
    width: number | null;
    height: number | null;
    megapixels: number | null;
    aspectRatio: number | null;
    mimeType: string;
    bits: number | null;
    fileSize: number | null;
    isTrueColor: boolean | null;
  };
}

export interface FilterOption {
  id: string;
  label: string;
  stageId?: string;
}

export interface PreCheckResult {
  status: 'pass' | 'warning' | 'fail';
  title: string;
  description: string;
}

export interface PiiFlaggingData {
  enabled: boolean;
  isPiiDetected: boolean;
  detectedTypes: string[];
  confidencePercent: number | null;
}

export interface DuplicateCheckEntry {
  method: string;
  isDuplicate: boolean;
  checkEnabled?: boolean;
  isAdvisory?: boolean;
}

export interface DuplicateCheckData {
  exact: DuplicateCheckEntry | null;
  semantic: DuplicateCheckEntry | null;
  similarity: DuplicateCheckEntry | null;
  isDuplicate: boolean;
}

export interface BlurFlaggingData {
  enabled: boolean;
  passed: boolean;
  isBlurDetected: boolean;
  details: string;
  confidencePercent: number | null;
}

export interface ValidationSummary {
  finalStatus: string;
  reasoning: string;
  failureReason: string | null;
  confidencePercent: number | null;
}

export const DEFAULT_IMAGE_INFO = {
  width: null as number | null,
  height: null as number | null,
  megapixels: null as number | null,
  aspectRatio: null as number | null,
  mimeType: '-',
  bits: null as number | null,
  fileSize: null as number | null,
  isTrueColor: null as boolean | null,
};

export const safeTrim = (value: unknown): string => String(value ?? '').trim();

export const formatTitleFromKey = (value: string): string =>
  safeTrim(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

export const normalizeStatusKey = (value: unknown): 'pass' | 'warning' | 'fail' => {
  const normalizedValue = safeTrim(value).toLowerCase();

  if (
    normalizedValue === 'pass' ||
    normalizedValue === 'passed' ||
    normalizedValue === 'full_pass' ||
    normalizedValue === 'full pass'
  ) {
    return 'pass';
  }

  if (
    normalizedValue === 'warning' ||
    normalizedValue === 'warnng' ||
    normalizedValue === 'warn'
  ) {
    return 'warning';
  }

  return 'fail';
};

export const getStatusLabel = (status: 'pass' | 'warning' | 'fail'): string => {
  if (status === 'pass') {
    return 'PASS';
  }

  if (status === 'warning') {
    return 'WARNING';
  }

  return 'FAIL';
};

export const getStatusSx = (status: 'pass' | 'warning' | 'fail') => {
  if (status === 'pass') {
    return { backgroundColor: '#16A34A', color: '#FFFFFF' };
  }

  if (status === 'warning') {
    return { backgroundColor: '#FACC15', color: '#111827' };
  }

  return { backgroundColor: '#DC2626', color: '#FFFFFF' };
};

export const formatRuntimeStatus = (status: string): string =>
  getStatusLabel(normalizeStatusKey(status));

export const extractPreCheckResults = (preChecksValue: unknown): PreCheckResult[] => {
  const normalizeEntry = (
    entry: Record<string, unknown>,
    index: number,
    fallbackTitle?: string
  ): PreCheckResult => {
    const title = safeTrim(
      entry.title ??
      entry.name ??
      entry.check_name ??
      entry.label ??
      (fallbackTitle ? formatTitleFromKey(fallbackTitle) : '') ??
      `Check ${index + 1}`
    );
    const description = safeTrim(
      entry.description ?? entry.detail ?? entry.message ?? entry.summary ?? entry.result_text ?? entry.details ?? '-'
    );

    return {
      status: normalizeStatusKey(entry.status ?? entry.result ?? entry.check_status ?? entry.outcome),
      title,
      description,
    };
  };

  if (preChecksValue && typeof preChecksValue === 'object') {
    const preCheckRecord = preChecksValue as Record<string, unknown>;

    const objectEntries = Object.entries(preCheckRecord)
      .filter(([entryKey, entryValue]) => {
        if (!entryValue || Array.isArray(entryValue)) {
          return false;
        }

        if (typeof entryValue !== 'object') {
          return false;
        }

        return !['status', 'result', 'message', 'summary', 'description'].includes(entryKey);
      })
      .map(([entryKey, entryValue], index) =>
        normalizeEntry(entryValue as Record<string, unknown>, index, entryKey)
      );

    if (objectEntries.length > 0) {
      return objectEntries;
    }
  }

  return [];
};
